// ============================================================
// auditStorage.js
// Save, load, list, and delete audits using localStorage
// Each audit is stored as a self-contained JSON blob
// Key format: osha_audit_{timestamp}
// ============================================================

const STORAGE_PREFIX = "osha_audit_";
const INDEX_KEY = "osha_audit_index";

/** Build the storage key for an audit */
function auditKey(id) {
  return `${STORAGE_PREFIX}${id}`;
}

/** Read the audit index (array of metadata objects) */
function readIndex() {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Write the audit index */
function writeIndex(index) {
  localStorage.setItem(INDEX_KEY, JSON.stringify(index));
}

/**
 * Save or overwrite an audit.
 * @param {string|null} id  - Existing audit ID to overwrite, or null to create new
 * @param {object} auditInfo
 * @param {array}  selectedCategories
 * @param {object} responses
 * @returns {string} The audit ID
 */
export function saveAudit(id, auditInfo, selectedCategories, responses) {
  const now = Date.now();
  const auditId = id || String(now);

  const payload = {
    id: auditId,
    savedAt: new Date().toISOString(),
    auditInfo,
    selectedCategories,
    responses,
  };

  localStorage.setItem(auditKey(auditId), JSON.stringify(payload));

  // Update index
  const index = readIndex().filter((m) => m.id !== auditId);
  index.unshift({
    id: auditId,
    savedAt: payload.savedAt,
    companyName: auditInfo.companyName || "Unnamed",
    facilityName: auditInfo.facilityName || "",
    auditDate: auditInfo.auditDate || "",
    auditorName: auditInfo.auditorName || "",
    categoryCount: selectedCategories.length,
  });
  writeIndex(index);

  return auditId;
}

/**
 * Load a single audit by ID.
 * @returns {object|null}
 */
export function loadAudit(id) {
  try {
    const raw = localStorage.getItem(auditKey(id));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * List all saved audits (metadata only, sorted newest first).
 * @returns {array}
 */
export function listAudits() {
  return readIndex();
}

/**
 * Delete an audit by ID.
 */
export function deleteAudit(id) {
  localStorage.removeItem(auditKey(id));
  writeIndex(readIndex().filter((m) => m.id !== id));
}

/**
 * Returns true if any audits have been saved.
 */
export function hasSavedAudits() {
  return readIndex().length > 0;
}
